import { useCallback } from 'react';
import { activityLogger } from '@/lib/activityLogger';
import { AUTH_EVENTS, SECURITY_EVENTS, DATA_EVENTS } from '@/lib/activityEvents';
import type { AllEventType } from '@/lib/activityEvents';

/**
 * React Hook for Activity Logging
 * Provides type-safe, convenient methods for logging from components
 */
export function useActivityLogger() {
  // Generic log function
  const log = useCallback(
    (
      event: AllEventType,
      metadata?: Record<string, any>,
      description?: string
    ) => {
      activityLogger.log({ event, metadata, description });
    },
    []
  );

  // Auth event helpers
  const logLoginSuccess = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.LOGIN_SUCCESS, metadata);
  }, []);

  const logLoginFailed = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.LOGIN_FAILED, metadata);
  }, []);

  const logLogout = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.LOGOUT, metadata);
  }, []);

  const logPasswordResetRequested = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.PASSWORD_RESET_REQUESTED, metadata);
  }, []);

  const logPasswordResetCompleted = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.PASSWORD_RESET_COMPLETED, metadata);
  }, []);

  const logPasswordResetFailed = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.PASSWORD_RESET_FAILED, metadata);
  }, []);

  const logOtpSent = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.OTP_SENT, metadata);
  }, []);

  const logOtpVerified = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.OTP_VERIFIED, metadata);
  }, []);

  const logOtpFailed = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.OTP_FAILED, metadata);
  }, []);

  const logEmailVerificationSent = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.EMAIL_VERIFICATION_SENT, metadata);
  }, []);

  const logPhoneVerificationCompleted = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(AUTH_EVENTS.PHONE_VERIFICATION_COMPLETED, metadata);
  }, []);

  // Security event helpers
  const logRateLimitWarning = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logSecurity(SECURITY_EVENTS.RATE_LIMIT_WARNING, metadata);
  }, []);

  const logRateLimitExceeded = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logSecurity(SECURITY_EVENTS.RATE_LIMIT_EXCEEDED, metadata);
  }, []);

  const logInvalidRedirect = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logSecurity(SECURITY_EVENTS.INVALID_REDIRECT, metadata);
  }, []);

  const logCsrfTokenInvalid = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logSecurity(SECURITY_EVENTS.CSRF_TOKEN_INVALID, metadata);
  }, []);

  const logSessionExpired = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logSecurity(SECURITY_EVENTS.SESSION_EXPIRED, metadata);
  }, []);

  // Data event helpers
  const logProfileUpdated = useCallback((metadata?: Record<string, any>) => {
    activityLogger.logAuth(DATA_EVENTS.PROFILE_UPDATED, metadata);
  }, []);

  const logUserCreated = useCallback((userId: string, metadata?: Record<string, any>) => {
    activityLogger.logData(DATA_EVENTS.USER_CREATED, 'user', userId, userId, metadata);
  }, []);

  const logUserDeleted = useCallback((userId: string, metadata?: Record<string, any>) => {
    activityLogger.logData(DATA_EVENTS.USER_DELETED, 'user', userId, userId, metadata);
  }, []);

  const logUserRoleUpdated = useCallback((userId: string, metadata?: Record<string, any>) => {
    activityLogger.logData(DATA_EVENTS.USER_ROLE_UPDATED, 'user', userId, userId, metadata);
  }, []);

  const logEmailUpdated = useCallback((userId: string, metadata?: Record<string, any>) => {
    activityLogger.logData(DATA_EVENTS.EMAIL_UPDATED, 'user', userId, userId, metadata);
  }, []);

  return {
    // Generic
    log,

    // Auth
    logLoginSuccess,
    logLoginFailed,
    logLogout,
    logPasswordResetRequested,
    logPasswordResetCompleted,
    logPasswordResetFailed,
    logOtpSent,
    logOtpVerified,
    logOtpFailed,
    logEmailVerificationSent,
    logPhoneVerificationCompleted,

    // Security
    logRateLimitWarning,
    logRateLimitExceeded,
    logInvalidRedirect,
    logCsrfTokenInvalid,
    logSessionExpired,

    // Data
    logProfileUpdated,
    logUserCreated,
    logUserDeleted,
    logUserRoleUpdated,
    logEmailUpdated,
  };
}
